import { useQueryClient } from '@tanstack/react-query'
import { Divider, type RefSelectProps } from 'antd'
import { useAtomValue } from 'jotai'
import {
  type ReactNode,
  type Ref,
  forwardRef,
  useCallback,
  useMemo,
  useState
} from 'react'
import { FaPlus } from 'react-icons/fa'

import { Button, Select } from '@/components/elements'
import { type SingleOrMultipleSelectProps } from '@/components/types'
import { MENU, STATUSES } from '@/constants/common/message.constants'
import { type ShipmentCategoryEntity } from '@/libs/api'
import { useSelectOnChangeHandler } from '@/libs/hooks'
import { selectedDcIdAtom } from '@/routes/_protected/$tenantKey/-states'

import { useShipmentCategoriesSelectQuery } from '../../hooks'
import { shipmentCategoriesQueryKeys } from '../../query-keys'
import { ShipmentCategoryCreateModal } from '../ShipmentCategoryCreateModal'

import { mapToShipmentCategoryFieldName } from './map-to-shipment-category-field-name'

type Props = SingleOrMultipleSelectProps<number, ShipmentCategoryEntity> & {
  tenantKey: string
  /** 拠点ID指定（指定しない場合は選択された拠点IDを使用して絞り込まれる） */
  dcId?: number
  /** 新規作成ボタンを非表示にするかどうか */
  hideCreateButton?: boolean
  /** 新規作成後のコールバック関数 */
  onCreate?: () => void
  shippingDate?: string
  /** 選択肢一覧の末尾に追加表示する要素（例: 操作ヒント） */
  dropdownFooter?: ReactNode
}

/**
 * 配送種別を選択するSelect
 */
export const ShipmentCategorySelect = forwardRef(
  (
    {
      placeholder = STATUSES.UNSELECTED,
      mode,
      onChange,
      onCreate,
      tenantKey,
      dcId,
      value,
      hideCreateButton,
      shippingDate,
      dropdownFooter,
      ...props
    }: Props,
    ref: Ref<RefSelectProps> | undefined
  ) => {
    const [isOpenCreateModal, setIsOpenCreateModal] = useState(false)

    const queryClient = useQueryClient()

    const handleChange = useSelectOnChangeHandler({ mode, onChange })

    const selectedDcId = useAtomValue(selectedDcIdAtom)
    if (dcId === undefined) {
      // 拠点IDが指定されていない場合は、選択された拠点IDを使用して絞り込む
      dcId = selectedDcId
    }

    const handleCreateSuccess = useCallback(
      async (shipmentCategory: ShipmentCategoryEntity) => {
        await queryClient.invalidateQueries({
          queryKey: shipmentCategoriesQueryKeys.select(tenantKey, dcId)
        })
        handleChange(shipmentCategory.id, shipmentCategory)
        setIsOpenCreateModal(false)

        if (onCreate) onCreate()
      },
      [queryClient, tenantKey, handleChange, onCreate, dcId]
    )

    const { data, isFetching, isLoading } = useShipmentCategoriesSelectQuery(
      tenantKey,
      { dcId, shippingDate }
    )

    const options = useMemo(() => {
      return data
        ?.sort((a, b) => a.code.localeCompare(b.code))
        ?.map((shipmentCategory) => ({
          ...shipmentCategory,
          // nameで指定すると利用側で取得できるoption.nameの値が変わるため、labelで指定
          label: mapToShipmentCategoryFieldName(shipmentCategory)
        }))
    }, [data])

    return (
      <>
        <Select
          customRef={ref}
          mode={mode}
          value={isLoading ? null : value}
          placeholder={placeholder}
          options={options}
          loading={isFetching}
          onChange={handleChange}
          {...props}
          dropdownRender={(menu) => (
            <div>
              {menu}
              {!hideCreateButton && (
                <>
                  <Divider className="mx-0 my-2" />
                  <div className="text-center">
                    <Button
                      icon={<FaPlus />}
                      onClick={() => setIsOpenCreateModal(true)}
                    >
                      {MENU.CREATING_SHIPMENT_CATEGORY}
                    </Button>
                  </div>
                </>
              )}
              {dropdownFooter}
            </div>
          )}
        />
        <ShipmentCategoryCreateModal
          open={isOpenCreateModal}
          tenantKey={tenantKey}
          onSuccess={handleCreateSuccess}
          onClose={() => setIsOpenCreateModal(false)}
        />
      </>
    )
  }
)
